import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import * as Dialog from '@radix-ui/react-dialog';
import { Archive, Eye, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  useOrders,
  SALES_STAGE_LABELS,
  SALES_STAGE_LIST,
  SOURCE_TYPE_LABELS,
  ORDER_TYPE_LABELS,
  TERMINAL_STAGES,
  type CustomerOrderDto,
} from '../../hooks/useOrders';
import { useAuth } from '@/hooks/useAuth';
import { CRM_PERMISSIONS, hasPermission } from '@/lib/crmPermissions';
import AccessDenied from '@/components/AccessDenied';
import {
  AdminDataTable,
  type AdminDataTableAction,
  type AdminDataTableColumn,
} from '@/components/business/AdminDataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type SalesStageFilter =
  | 'lead_in'
  | 'link_building'
  | 'trust_building'
  | 'needs_discovery'
  | 'proposal_quote'
  | 'marketing'
  | 'delivering'
  | 'maintaining'
  | 'repurchased'
  | 'referred'
  | 'lost';

const ARCHIVED_STAGE: SalesStageFilter = 'lost';

export function OrderList() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { loading, error, listOrders, transitionStage } = useOrders();
  const [orders, setOrders] = useState<CustomerOrderDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  // Issue #345 audit fix: a previous version also held a `keyword` text
  // input which filtered the current page in memory by orderNo/title. That
  // misled users when matches lived on later pages while the server `total`
  // already counted them. Until the backend exposes a `search`/`q` query
  // parameter for orders, the toolbar only renders the 销售阶段 select that
  // already round-trips to the server.
  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    stage: '',
  });

  // Issue #345 audit fix: list visibility, create entry, and row actions now
  // gate on the dedicated crm.order:* namespace — backend OrderController
  // enforces the same strings via @RequirePermission, so the UI guard must
  // match or read-only customer accounts would still see edit/archive entries
  // that the backend would 403-reject.
  const canRead = hasPermission(permissions, CRM_PERMISSIONS.ORDER_READ);
  const canCreate = hasPermission(permissions, CRM_PERMISSIONS.ORDER_CREATE);
  const canUpdate = hasPermission(permissions, CRM_PERMISSIONS.ORDER_UPDATE);

  const [archiveTarget, setArchiveTarget] = useState<{
    readonly order: CustomerOrderDto;
    readonly sessionKey: number;
  } | null>(null);
  const archiveSessionRef = useRef(0);
  const [archiving, setArchiving] = useState(false);

  const stageFilter = (searchValues['stage'] ?? '') as SalesStageFilter | '';

  const refresh = useCallback(async () => {
    if (!canRead) return;
    try {
      const result = await listOrders({
        salesStage: stageFilter !== '' ? stageFilter : undefined,
        page,
        pageSize,
      });
      setOrders(result.data);
      setTotal(result.total);
    } catch {
      /* hook surfaces error */
    }
  }, [canRead, listOrders, page, stageFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Issue #345 audit fix: no client-side text filter on top of server-
  // paginated orders — see the `searchValues` declaration above for rationale.
  const visibleRows = orders;

  const columns: ReadonlyArray<AdminDataTableColumn<CustomerOrderDto>> = useMemo(
    () => [
      {
        key: 'orderNo',
        header: '订单编号',
        alwaysVisible: true,
        render: (o) => <span className="font-mono">{o.orderNo}</span>,
      },
      {
        key: 'title',
        header: '标题',
        render: (o) => o.title,
      },
      {
        key: 'orderType',
        header: '类型',
        render: (o) => ORDER_TYPE_LABELS[o.orderType] ?? o.orderType,
      },
      {
        key: 'salesStage',
        header: '销售阶段',
        render: (o) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              TERMINAL_STAGES.has(o.salesStage)
                ? o.salesStage === 'lost'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-green-100 text-green-800'
                : 'bg-blue-100 text-blue-800'
            }`}
          >
            {SALES_STAGE_LABELS[o.salesStage] ?? o.salesStage}
          </span>
        ),
      },
      {
        key: 'sourceType',
        header: '来源',
        defaultHidden: true,
        render: (o) =>
          o.sourceType ? SOURCE_TYPE_LABELS[o.sourceType] ?? o.sourceType : '—',
      },
      {
        key: 'risk',
        header: '风险',
        render: (o) =>
          o.isHighRisk ? (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                o.highRiskConfirmed
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {o.highRiskConfirmed ? '高风险(已确认)' : '高风险(待确认)'}
            </span>
          ) : (
            <span className="text-gray-400 text-xs">—</span>
          ),
      },
      {
        key: 'createdAt',
        header: '创建时间',
        render: (o) => new Date(o.createdAt).toLocaleDateString('zh-CN'),
      },
    ],
    [],
  );

  const rowActions: ReadonlyArray<AdminDataTableAction<CustomerOrderDto>> =
    useMemo(
      () => [
        {
          key: 'view',
          label: '查看',
          icon: <Eye className="h-3.5 w-3.5" />,
          onSelect: (o) => navigate(`/crm/orders/${o.id}`),
        },
        {
          key: 'edit',
          label: '编辑',
          icon: <Pencil className="h-3.5 w-3.5" />,
          visible: () => canUpdate,
          onSelect: (o) => navigate(`/crm/orders/${o.id}`),
        },
        {
          key: 'archive',
          label: '归档（标记流失）',
          icon: <Archive className="h-3.5 w-3.5" />,
          destructive: true,
          // 财务/订单数据不可物理删除（AI GUIDE §6）。归档 = 把销售阶段转为终态
          // lost，对应订单状态机的提前退出路径；后端 PUT /orders/{id}/stage 走
          // 既有的乐观锁 + 状态机校验。Issue #345 audit fix：lost 终态属于需求
          // 文档要求记录流失原因/复核结论的终态，前端通过弹窗收集必填 reason
          // 并随 transition DTO 透传到后端。
          visible: (o) => canUpdate && !TERMINAL_STAGES.has(o.salesStage),
          onSelect: (o) => {
            archiveSessionRef.current += 1;
            setArchiveTarget({ order: o, sessionKey: archiveSessionRef.current });
          },
        },
      ],
      [canUpdate, navigate],
    );

  if (!canRead) return <AccessDenied resource="客户订单" />;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">客户订单</h1>
        {canCreate && (
          <Button
            type="button"
            onClick={() => navigate('/crm/orders/create')}
          >
            新建订单
          </Button>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      <AdminDataTable<CustomerOrderDto>
        testIdPrefix="crm-orders"
        columns={columns}
        rows={visibleRows}
        isLoading={loading}
        emptyMessage="暂无订单"
        filters={{
          fields: [
            {
              kind: 'select',
              name: 'stage',
              label: '销售阶段',
              options: SALES_STAGE_LIST.map((s) => ({
                value: s,
                label: SALES_STAGE_LABELS[s] ?? s,
              })),
            },
          ],
          values: searchValues,
          onSubmit: (next) => {
            setSearchValues(next);
            setPage(1);
          },
          onReset: () => {
            setSearchValues({ stage: '' });
            setPage(1);
          },
        }}
        pagination={{
          page,
          pageSize,
          total,
          onPageChange: setPage,
        }}
        rowActions={rowActions}
      />

      {archiveTarget && (
        <ArchiveOrderDialog
          key={`archive-order-${archiveTarget.order.id}-${archiveTarget.sessionKey}`}
          order={archiveTarget.order}
          submitting={archiving}
          onCancel={() => setArchiveTarget(null)}
          onSubmit={async (reason) => {
            setArchiving(true);
            try {
              await transitionStage({
                id: archiveTarget.order.id,
                nextStage: ARCHIVED_STAGE,
                expectedVersion: archiveTarget.order.version,
                reason,
              });
              toast.success(`订单「${archiveTarget.order.orderNo}」已归档`);
              setArchiveTarget(null);
              await refresh();
            } catch (err) {
              toast.error(err instanceof Error ? err.message : '归档失败');
            } finally {
              setArchiving(false);
            }
          }}
        />
      )}
    </div>
  );
}

interface ArchiveOrderDialogProps {
  readonly order: CustomerOrderDto;
  readonly submitting: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (reason: string) => Promise<void>;
}

function ArchiveOrderDialog({
  order,
  submitting,
  onCancel,
  onSubmit,
}: ArchiveOrderDialogProps) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const trimmed = reason.trim();
  const tooShort = trimmed.length < 4;
  const showError = touched && tooShort;
  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg"
          data-testid="crm-order-archive-dialog"
        >
          <Dialog.Title className="mb-1 text-lg font-semibold">
            归档（标记流失）
          </Dialog.Title>
          <Dialog.Description className="mb-3 text-sm text-gray-500">
            将订单「{order.orderNo}」标记为流失（lost）。不会物理删除任何财务
            记录；需求文档要求记录流失原因，请填写后提交。
          </Dialog.Description>
          <div className="space-y-1">
            <Label htmlFor="crm-order-archive-reason">流失原因（必填）</Label>
            <Input
              id="crm-order-archive-reason"
              data-testid="crm-order-archive-reason"
              autoComplete="off"
              placeholder="如 客户改约同业方案，预算缩减"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
            />
            {showError && (
              <p
                className="text-xs text-red-600"
                data-testid="crm-order-archive-reason-error"
              >
                请填写至少 4 个字的流失原因，便于复核
              </p>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="crm-order-archive-cancel"
              onClick={onCancel}
            >
              取消
            </Button>
            <Button
              type="button"
              disabled={submitting || tooShort}
              data-testid="crm-order-archive-submit"
              onClick={async () => {
                setTouched(true);
                if (tooShort) return;
                await onSubmit(trimmed);
              }}
            >
              {submitting ? '提交中…' : '确认归档'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
